import { usePartnerDetails } from './usePartnerDetails.ts';
import { usePartnerPlans, usePlans } from './usePartnerPlans.ts';
import { usePricingTiers } from './usePartnerSettings.ts';

interface AvailablePlan {
  id: string;
  name: string;
  identifier: string;
  price: number;
  billing_period: string;
  features: string[];
  is_custom: boolean;
}

interface UsePartnerSubAccountCreationReturn {
  availablePlans: AvailablePlan[] | undefined;
  canCreateSubAccounts: boolean;
  isLoading: boolean;
  hasCustomSubscriptionPlans: boolean;
  hasCustomBoostPackPricing: boolean;
  isWhiteLabel: boolean;
}

export const usePartnerSubAccountCreation = (): UsePartnerSubAccountCreationReturn => {
  const { data: partnerDetails } = usePartnerDetails();
  const partnerType = partnerDetails?.partner_type as 'co_branded' | 'white_label';
  const isWhiteLabel = partnerType === 'white_label';

  // Get partner plans and pricing tiers for validation
  const { data: generalPartnerPlans, isLoading: plansLoading } = usePartnerPlans(partnerType);
  const { data: customSubscriptionPlans, isLoading: subscriptionPlansLoading } = usePlans();
  const { data: customPricingTiers, isLoading: pricingTiersLoading } = usePricingTiers();

  // For white-label partners, use only their custom plans. For co-branded, use general partner plans.
  const availablePlans = isWhiteLabel && customSubscriptionPlans ? 
    customSubscriptionPlans.map(plan => ({
      id: plan.id,
      name: plan.name,
      identifier: plan.plan_identifier,
      price: plan.price_monthly,
      billing_period: 'month',
      features: [], // Custom plans don't have features field in the interface
      is_custom: true
    })) : generalPartnerPlans;

  // Validation for white-label partners
  const hasCustomSubscriptionPlans = customSubscriptionPlans && customSubscriptionPlans.length > 0;
  const hasCustomBoostPackPricing = customPricingTiers && customPricingTiers.length > 0;
  const canCreateSubAccounts = !isWhiteLabel || (hasCustomSubscriptionPlans && hasCustomBoostPackPricing);

  return {
    availablePlans,
    canCreateSubAccounts: canCreateSubAccounts || false,
    isLoading: plansLoading || subscriptionPlansLoading || pricingTiersLoading || false,
    hasCustomSubscriptionPlans: hasCustomSubscriptionPlans || false,
    hasCustomBoostPackPricing: hasCustomBoostPackPricing || false,
    isWhiteLabel: isWhiteLabel || false,
  };
};
